import { computed, inject, Injectable, Injector, signal } from '@angular/core';
import { Store } from '@ngxs/store';
import { ErrorHandlerV2Service, UserModel, UserState } from '@gea/digital-ui-lib';
import { isEqual } from 'lodash';
import { catchError, map, of, tap } from 'rxjs';

import { MembershipModel, MembershipStateModel } from '@shared/models';
import { User } from '@shared/models/user.model';
import { UserService } from '@shared/services';
import { OrganizationsStore } from '../organizations';

const INITIAL_STATE: MembershipStateModel = {
  memberships: undefined,
};

@Injectable({ providedIn: 'root' })
export class MembershipsStore {
  private readonly ngxsStore = inject(Store);
  private readonly usersApiService = inject(UserService);
  private readonly errorHandler = inject(ErrorHandlerV2Service);
  private readonly injector = inject(Injector);

  private readonly state = signal<MembershipStateModel>(INITIAL_STATE);

  readonly memberships = computed<MembershipModel[] | undefined>(() => this.state().memberships, { equal: isEqual });

  /**
   * Loads the memberships of the current user and then refreshes the organizations.
   * Does nothing when the user is not yet available.
   */
  fetchMemberships(): void {
    const user = this.ngxsStore.selectSnapshot<UserModel>(UserState.user);

    if (!user?.userId || !user?.language) {
      return;
    }

    this.usersApiService
      .getUser(user.userId)
      .pipe(
        map((user: User) => user.memberships ?? []),
        tap((memberships: MembershipModel[]) => {
          this.patch({ memberships });
          this.injector.get(OrganizationsStore).fetchOrganizations();
        }),
        catchError((error) => {
          this.errorHandler.handleError(error);
          this.patch({ memberships: [] });
          return of([]);
        })
      )
      .subscribe();
  }

  private patch(partial: Partial<MembershipStateModel>): void {
    this.state.update((current) => ({ ...current, ...partial }));
  }
}
